import moment from "moment";
import { differenceInMinutes } from "date-fns";

const POLL_INTERVAL_NORMAL = 5 * 60 * 1000; // 5 min
const POLL_INTERVAL_CLOSE = 2 * 60 * 1000; // 2 min
const POLL_WINDOW_START = 120; // Start polling 2h before departure
const POLL_WINDOW_CLOSE = 20; // Switch to fast polling 20 min before
const STOP_AFTER_CTOT = -5; // Stop polling 5min after CTOT
const DEBUG = true;

// Helper to parse CTOT time and create datetime
const parseCTOTDateTime = (ctotString, flightDate) => {
    const ctotTime = ctotString.replace(":", "");
    const hours = parseInt(ctotTime.substring(0, 2), 10);
    const minutes = parseInt(ctotTime.substring(2, 4), 10);

    return moment
        .utc(flightDate)
        .hours(hours)
        .minutes(minutes)
        .seconds(0)
        .toDate();
};

export const pollCTOTDataHelper = async ({
    isInitial = false,
    flights = [],
    isOnline = true,
    hasErrorStatus = false,
    getLocalData,
    getFlight,
    getFlightCTOT,
    getFlightCTOTCrew,
    dbKeyCTOT,
    dbKeyCTOTCrew,
}) => {
    const now = moment.utc().toDate();
    let nextPollDelay = POLL_INTERVAL_NORMAL;

    if (isInitial) {
        if (DEBUG) console.log(`[CTOT] Initial - next poll in 5min`);
        return nextPollDelay;
    }

    if (!isOnline) {
        if (DEBUG) console.log(`[CTOT] Offline - skipping`);
        return nextPollDelay;
    }

    if (hasErrorStatus) {
        if (DEBUG) console.log(`[CTOT] Error status - skipping`);
        return nextPollDelay;
    }

    if (DEBUG) console.log(`[CTOT] Polling ${flights.length} flight(s)...`);

    for (const flight of flights) {
        const ctotCrewData = await getLocalData(dbKeyCTOTCrew(flight.id));

        // Check for slot cancellation
        const hasSlcCancellation = ctotCrewData?.ctotMsg?.includes("SLC") ||
            ctotCrewData?.ctotMsg?.includes("SLOT CANCELLED");
        if (hasSlcCancellation) {
            if (DEBUG) console.log(`[CTOT] ${flight.id}: SLC - stop polling`);
            continue;
        }

        // Determine reference time: CTOT if available, otherwise STD
        let referenceDateTime = flight.departureDateTime || now;
        const hasCTOT = ctotCrewData?.ctot && ctotCrewData.ctot !== "CANCELLED";

        if (hasCTOT) {
            try {
                referenceDateTime = parseCTOTDateTime(ctotCrewData.ctot, flight.departureDateTime);
                if (DEBUG) console.log(`[CTOT] ${flight.id}: Using CTOT ${ctotCrewData.ctot}`);
            } catch (error) {
                console.warn(`[CTOT] ${flight.id}: Parse error, using STD`, error);
            }
        } else if (DEBUG) {
            console.log(`[CTOT] ${flight.id}: Using STD (no CTOT)`);
        }

        const minutesFromDeparture = differenceInMinutes(referenceDateTime, now);

        // Skip if too early (>2h before departure)
        if (minutesFromDeparture > POLL_WINDOW_START) {
            if (DEBUG) console.log(`[CTOT] ${flight.id}: ${minutesFromDeparture}min - too early`);
            continue;
        }

        // Stop polling logic depends on whether we have CTOT
        const stopThreshold = hasCTOT ? STOP_AFTER_CTOT : 0;
        if (minutesFromDeparture < stopThreshold) {
            if (DEBUG) console.log(`[CTOT] ${flight.id}: Departed${hasCTOT ? '+5min' : ''} - stop polling`);
            continue;
        }

        // Fetch flight data and check for CTOT changes
        const flightData = await getFlight(flight);
        const previousCtot = await getLocalData(dbKeyCTOT(flight.id));

        await getFlightCTOT(flightData?.allData, flight.id);

        const newCtot = await getLocalData(dbKeyCTOT(flight.id));
        const hasChanged = previousCtot && newCtot && previousCtot !== newCtot;

        if (hasChanged) {
            console.log(`[CTOT] ${flight.id}: ⚠️ CHANGED: ${previousCtot} → ${newCtot}`);
            await getFlightCTOTCrew(flightData?.allData, flight.id);
        } else if (DEBUG) {
            console.log(`[CTOT] ${flight.id}: No change (${newCtot || "N/A"})`);
        }

        // Adjust poll interval: 2min when close, 5min otherwise
        const isClose = minutesFromDeparture <= POLL_WINDOW_CLOSE && minutesFromDeparture >= STOP_AFTER_CTOT;
        nextPollDelay = Math.min(nextPollDelay, isClose ? POLL_INTERVAL_CLOSE : POLL_INTERVAL_NORMAL);
    }

    if (DEBUG) console.log(`[CTOT] Next poll in ${(nextPollDelay / 60000).toFixed(1)}min`);

    return nextPollDelay;
};

